import { View, StyleSheet } from "react-native";
import { useState, useEffect } from "react";
import DefaultLayout from "../layouts/DefaultLayout";
import CustomText from "../components/common/general/CustomText/CustomText.js";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { UnChecked, Checked } from "../components/svgs/common";
import { useGetTenantSchemaQuery, useGetTenantQuery, GetTenantDocument } from "../graphql/generated/graphql";
import { ScrollView } from "react-native-gesture-handler";
import { useNavigation, useRoute } from "@react-navigation/native";
import CustomTextInput from "../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import Loader from "../components/common/status/Loader/Loader";
import Divider from "../components/common/layout/Divider/Divider";
import CustomModal from "../components/common/general/CustomModal/CustomModal.js";
import { useUpdateTenantMutation } from "../graphql/generated/graphql";

const deepEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === "object") {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => deepEqual(v, b[i]));
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }
  return false;
};

const DynamicForm = ({ schema, originalValues, values, onValueChange, onSavePressed }) => {
  const renderField = (key, field, values, originalValues, handleChange) => {
    const isDiff = !!originalValues && !deepEqual(originalValues[key], values[key]);

    const labelStyle = isDiff ? { color: "#d9534f" } : {};
    const labelWeight = isDiff ? "ultraBold" : "medium";

    switch (field.type) {
      case "integer":
        return (
          <View key={key} style={styles.fieldContainer}>
            <CustomText
              role="header"
              text={key}
              size="m"
              weight={labelWeight}
              style={labelStyle}
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            <CustomTextInput
              value={values[key] != null ? String(values[key]) : ""}
              onChangeText={(text) => {
                const parsed = parseInt(text, 10);
                handleChange(key, !isNaN(parsed) ? parsed : "");
              }}
              keyboardType="numeric"
            />
            <Divider size="l" />
          </View>
        );
      case "string":
        return (
          <View key={key} style={styles.fieldContainer}>
            <CustomText
              role="header"
              text={key}
              size="m"
              weight={labelWeight}
              style={labelStyle}
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            <CustomTextInput value={values[key] || ""} onChangeText={(text) => handleChange(key, text)} multiline />
            <Divider size="l" />
          </View>
        );
      case "boolean":
        return (
          <View key={key} style={styles.fieldContainer}>
            <View style={styles.booleanRow}>
              <CustomText
                role="header"
                text={key}
                size="m"
                weight={labelWeight}
                style={labelStyle}
                useTranslationText={false}
              />
              <CustomButton
                styleType="transparent"
                leftIcon={values[key] ? <Checked /> : <UnChecked />}
                onPress={() => handleChange(key, !values[key])}
              />
            </View>
            <Divider size="l" />
          </View>
        );
      case "array":
        return (
          <View key={key} style={styles.fieldContainer}>
            <CustomText
              role="header"
              text={key}
              size="m"
              weight={labelWeight}
              style={labelStyle}
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            {Array.isArray(values[key]) &&
              values[key].map((item, idx) => (
                <View key={idx} style={styles.arrayItemRow}>
                  <CustomTextInput
                    value={String(item)}
                    onChangeText={(text) => {
                      const arr = [...values[key]];
                      arr[idx] = text;
                      handleChange(key, arr);
                    }}
                  />
                  <CustomButton
                    text="Remove"
                    styleType="danger"
                    size="s"
                    onPress={() => {
                      const arr = [...values[key]];
                      arr.splice(idx, 1);
                      handleChange(key, arr);
                    }}
                  />
                </View>
              ))}
            <CustomButton
              text="Add"
              size="s"
              styleType="primary"
              onPress={() => handleChange(key, [...(values[key] || []), ""])}
            />
            <Divider size="l" />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {Object.entries(schema.properties).map(([key, field]) =>
        renderField(key, field, values, originalValues, onValueChange)
      )}
      <CustomSpacing size="l" />
      <CustomButton text="Save Changes" styleType="lightGreen" onPress={onSavePressed} />
    </>
  );
};

export default function TenantSettingsDashboardView() {
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params;

  const [tenantSchema, setTenantSchema] = useState(null);
  const [visible, setVisible] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [values, setValues] = useState({});

  const { data: schemaData } = useGetTenantSchemaQuery();
  const { data: tenantData } = useGetTenantQuery({ variables: { id: params.id } });

  const [updateTenant, { error: updateTenantError }] = useUpdateTenantMutation({
    refetchQueries: [
      {
        query: GetTenantDocument,
        variables: { id: params.id },
      },
    ],
  });

  useEffect(() => {
    if (schemaData?.getTenantSchema?.data) {
      setTenantSchema(JSON.parse(schemaData.getTenantSchema.data));
    }
  }, [schemaData]);

  useEffect(() => {
    if (tenantData?.getTenant?.data?.[0]) {
      const initial = tenantData.getTenant.data[0];
      setOriginalValues(initial);
      setValues(initial);
    }
  }, [tenantData]);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const openModal = () => setVisible(true);
  const closeModal = () => setVisible(false);

  const handleSubmit = async () => {
    const diff = Object.keys(values).reduce((acc, key) => {
      if (!deepEqual(originalValues[key], values[key])) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    try {
      const { data } = await updateTenant({ variables: { id: params.id, tenant: diff } });
      if (data?.updateTenant?.success) {
        closeModal();
        navigation.navigate("Tenant Dashboard");
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!tenantSchema || !tenantData) {
    return <Loader />;
  }

  const changedKeys = Object.keys(values).filter((key) => !deepEqual(originalValues[key], values[key]));

  return (
    <DefaultLayout>
      <CustomText
        role="header"
        text="Tenant Settings"
        size="l"
        textType="display"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="l" />
      <CustomText role="header" text={values.tenantDomain} size="xxl" weight="ultraBold" useTranslationText={false} />
      <CustomSpacing size="l" />

      <ScrollView contentContainerStyle={styles.formContainer}>
        <DynamicForm
          schema={tenantSchema.definitions.Tenant}
          originalValues={originalValues}
          values={values}
          onValueChange={handleChange}
          onSavePressed={openModal}
        />
      </ScrollView>

      <CustomModal
        visible={visible}
        style={styles.modal}
        onRequestClose={closeModal}
        showCloseButton
        title="Confirm Changes">
        <CustomText role="header" text="You are about to update:" size="m" weight="medium" useTranslationText={false} />
        <Divider size="m" />
        <ScrollView style={styles.modalList}>
          {changedKeys.map((key) => (
            <View key={key} style={styles.modalItem}>
              <CustomText role="header" text={key} size="m" weight="medium" useTranslationText={false} />
              <CustomText
                role="header"
                text={JSON.stringify(values[key])}
                size="m"
                style={{ color: "#d9534f" }}
                useTranslationText={false}
              />
              <Divider size="s" />
            </View>
          ))}
        </ScrollView>
        {updateTenantError && (
          <>
            <CustomText
              role="header"
              text="Error updating tenant. Please retry."
              size="m"
              style={{ color: "#d9534f" }}
            />
            <Divider size="m" />
          </>
        )}
        <CustomButton text="Confirm" styleType="lightGreen" onPress={handleSubmit} />
        <CustomSpacing size="m" />
        <CustomButton text="Cancel" styleType="danger" onPress={closeModal} />
      </CustomModal>
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    paddingBottom: 40,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  booleanRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 8,
  },
  arrayItemRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  modal: {
    width: "90%",
    height: "70%",
  },
  modalList: {
    maxHeight: 200,
    marginVertical: 8,
  },
  modalItem: {
    marginBottom: 8,
  },
});
